import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { type GridRenderCellParams } from '@mui/x-data-grid'
import { PlusIcon } from '@dhl-ui/icons'
import { Pagination, type GridColDef } from '@dhl/components'
import { Box, Button, Grid, MenuItem, Paper, Select } from '@dhl-ui/components'
import { useSessionStorage } from '@dhl/use-storage'
import { useGetUsers, UserType, type UserType as UserTypeType, type RoleNameDto } from '@dhl/mdp-api'

import { links } from '~/AppRoutes'
import {
  AdminDataGrid,
  lowercaseTransform,
  renderUserName,
  SearchTextField,
  useNavigateWithSearchString,
  useSearchAndPage,
} from '~/common'
import { convertToUser, getUserTypeLabel, type User } from '~/modules/users/model'
import { RoleTags } from '~/modules/users/components'
import { isProbablyDeleted } from '~/modules/users/helpers'

export const UserListPage = () => {
  const navigateWithSearchString = useNavigateWithSearchString()
  const { searchAndPageParams, handleSearch, page, pageSize, setPage, setPageSize, search } = useSearchAndPage()
  const emptyUserTypeFilter = 'none'
  const [storedUserType, setStoredUserType] = useSessionStorage<string>('user-list-user-type', emptyUserTypeFilter)
  const { t } = useTranslation()

  const { data, isFetching } = useGetUsers(
    {
      ...searchAndPageParams,
      userType: storedUserType !== emptyUserTypeFilter ? [storedUserType as UserTypeType] : undefined,
    },
    {
      query: {
        select: ({ content = [], totalCount }) => ({ content: content.map(convertToUser), totalCount }),
      },
    }
  )

  const handleRowClick = ({ id }: User) => {
    const link = links.userDetailLink(id)
    navigateWithSearchString(link, search)
  }

  const handleUserTypeChange = (event: any) => {
    const { value } = event.target
    setStoredUserType(value)
  }

  const renderRoles = ({ value = [] }: GridRenderCellParams<User, RoleNameDto[]>) => {
    return <RoleTags roles={value} />
  }

  const renderUserMailOne = (value: string) => {
    if (!value) return ''
    const email = value
    return email.startsWith('deleted') ? `🗑️${t('admin.usersPage.userHeader.deletedUser')}` : email
  }

  const renderUserEmail = ({ value: email }: GridRenderCellParams<User>) => {
    return isProbablyDeleted(email) ? (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        &laquo;{t('admin.usersPage.userHeader.deletedUser')}&raquo;
      </div>
    ) : (
      <span>{renderUserMailOne(email)}</span>
    )
  }

  const formatDateTime = (value: string | undefined) => {
    return value ? dayjs(value).format('DD MMMM YYYY') : ''
  }

  const renderUserType = (value: UserType) => {
    return getUserTypeLabel(value)
  }

  const columns: GridColDef<User>[] = [
    {
      field: 'name',
      headerName: t('admin.usersPage.userHeader.name'),
      flex: 0,
      width: 200,
      minWidth: 200,
      maxWidth: 300,
      valueGetter: (_, user) => renderUserName(user),
    },
    {
      field: 'email',
      headerName: t('admin.usersPage.userHeader.emailAddress'),
      flex: 1,
      width: 250,
      minWidth: 200,
      maxWidth: 350,
      renderCell: renderUserEmail,
    },
    {
      field: 'userType',
      headerName: t('admin.usersPage.userHeader.type'),
      flex: 0,
      width: 150,
      valueGetter: renderUserType,
    },
    {
      field: 'roles',
      headerName: t('admin.usersPage.userHeader.roles'),
      flex: 1,
      renderCell: renderRoles,
    },
    {
      field: 'timeRegistered',
      headerName: t('admin.usersPage.userHeader.createdOn'),
      flex: 0,
      width: 200,
      minWidth: 200,
      maxWidth: 300,
      valueGetter: formatDateTime,
    },
    {
      field: 'timeLastLogin',
      headerName: t('admin.usersPage.userHeader.lastLogin'),
      flex: 0,
      width: 200,
      minWidth: 200,
      maxWidth: 300,
      valueGetter: formatDateTime,
    },
  ]

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" data-testid="allusers-page">
        <Grid sx={{ display: 'flex' }}>
          <SearchTextField onChange={handleSearch} debounceDelayMsec={600} transform={lowercaseTransform} />
          <Select
            name="userType"
            label={t('admin.userType.selectUserType')}
            value={storedUserType}
            onChange={handleUserTypeChange}
            sx={{ ml: 1, minWidth: 180 }}
          >
            <MenuItem value="none">{t('admin.userType.all')}</MenuItem>
            {Object.keys(UserType).map((value) => (
              <MenuItem key={value} value={value}>
                {t(`admin.userType.${value}`)}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid>
          <Button
            data-testid="add-allusers"
            color="primary"
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => navigateWithSearchString(links.userAddLink())}
          >
            {t('admin.usersPage.userHeader.addUser')}
          </Button>
        </Grid>
      </Grid>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            loading={isFetching}
            sx={{
              '& .MuiDataGrid-cell:hover': {
                cursor: 'pointer !important',
              },
            }}
            data-testid="allusers-table"
            rows={data?.content ?? []}
            columns={columns}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={data?.totalCount || 0}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={setPageSize}
            hideTotal
          />
        </Box>
      </Box>
    </>
  )
}
